import React, {useEffect, useRef, useState} from 'react';
import {
    AppBar,
    Autocomplete,
    Box,
    Button,
    CircularProgress,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Divider,
    FormControlLabel,
    FormGroup,
    IconButton,
    Switch,
    Tab,
    Tabs,
    TextField,
    Toolbar,
    Typography
} from "@mui/material";
import {useTranslation} from "react-i18next";
import {DropResult} from "@hello-pangea/dnd";
import './VoohForm.scss'
import {useTheme} from "@mui/material/styles";
import {MediabankResource, MediabankResourceTableRow} from "../../mediabank/types";
import {
    useCreateMediaTemplateMutation,
    useCreateVoohPlaylistMutation,
    useDeleteMediaTemplateMutation,
    useGetMediaTemplatesQuery,
    useGetVoohPlaylistQuery,
    useLazyGetResourcesMetaDataQuery,
    useReplanVoohMutation,
    useUpdateVoohPlaylistMutation
} from "../api/voohApiSlice";
import {ResourceMetaData, ScheduleRow, VoohCategory, VoohItem, VoohItemType, VoohPlaylistType} from "../types";
import {ResourceName} from "../../auth/types";
import ability from "../../../utils/ability";
import {useGetCmEnumsQuery} from "../../../app/appApiSlice";
import FormControl from "@mui/material/FormControl";
import InputLabel from "@mui/material/InputLabel";
import Select from "@mui/material/Select";
import MenuItem from "@mui/material/MenuItem";
import {useDispatch, useSelector} from "react-redux";
import {initialVoohFormState, selectVoohForm, setVoohForm} from "../store/voohSlice";
import {useGetMediaTreeQuery, useGetTagsQuery} from "../../drafts/api/draftsApiSlice";
import {MediaFormat, MediaNode} from "../../drafts/types";
import Chip from "@mui/material/Chip";
import CheckboxTree from "react-checkbox-tree";
import {ClearIcon, DatePicker} from "@mui/x-date-pickers";
import dayjs from "dayjs";
import {
    DEFAULT_ADVERTISEMENTS,
    MAX_NOMINAL_DURATION,
    MIN_ADVERTISEMENTS,
    MIN_NOMINAL_DURATION
} from "../../../config/vooh";
import useDebounce from "../../../hooks/useDebounce";
import {formatDuration, getUuid} from "../../../config/general";

import CloseIcon from '@mui/icons-material/Close';
import {enqueueSnackbar} from "notistack";
import ConfirmDialog from "../../shared/confirmDialog/ConfirmDialog";
import OwnershipSelect from "../../shared/ownershipSelect/OwnershipSelect";
import {parseOwnershipPrincipalKey} from "../../shared/ownershipSelect/principalKey";
import {useNavigate, useParams, useSearchParams} from "react-router-dom";
import {ApiTags, baseApi} from "../../../app/api/baseApi";
import VoohDragAndDropTable from "../voohDragAndDropTable/VoohDragAndDropTable";
import MediabankFileSelectDialog from "../../shared/mediabankFileSelectDialog/MediabankFileSelectDialog";
import DescriptionOutlinedIcon from "@mui/icons-material/DescriptionOutlined";
import {Thumbnail} from "../../../config/images";
import Link from "@mui/material/Link";
import {useLazyGetMediabankResourceQuery} from "../../mediabank/api/mediabankResourcesApiSlice";
import UsedMediaTable from "../usedMediaTable/usedMediaTable";

type Order = 'asc' | 'desc';


interface EnhancedTableProps {
    numSelected: number;
    onRequestSort: (event: React.MouseEvent<unknown>, property: keyof MediabankResourceTableRow) => void;
    onSelectAllClick: (event: React.ChangeEvent<HTMLInputElement>) => void;
    order: Order;
    orderBy: string;
    rowCount: number;
}

interface EnhancedTableToolbarProps {
    numSelected: number;
}

interface HeadCell {
    disablePadding: boolean;
    id: keyof MediabankResourceTableRow;
    label: string;
    numeric: boolean;
    sort: boolean;
    width: string;
}

const duplicatedRowsInitialData = {
    destinationIndex: undefined,
    mediabankResources: [],
    duplicatedRows: [],
    newRows: []
}

const VoohForm = () => {

    const mediabankBreadcrumbsInitialState = [{
        dirName: 'Katalog główny',
        resourceId: null
    }]

    const headCells: readonly HeadCell[] = [
        {
            id: 'name',
            numeric: false,
            disablePadding: true,
            label: 'Nazwa folderu',
            sort: true,
            width: '34'
        },
        {
            id: 'byteSize',
            numeric: false,
            disablePadding: true,
            label: 'Rozmiar',
            sort: true,
            width: '3'
        },
        {
            id: 'metadata',
            numeric: false,
            disablePadding: true,
            label: 'Rozdzielczość',
            sort: false,
            width: '8'
        },
        {
            id: 'tagList',
            numeric: true,
            disablePadding: false,
            label: 'Tagi',
            sort: false,
            width: '15'
        },
        {
            id: 'categoryId',
            numeric: true,
            disablePadding: false,
            label: 'Kategoria',
            sort: false,
            width: '20'
        },
        {
            id: 'updatedAt',
            numeric: true,
            disablePadding: false,
            label: 'Data wgrania',
            sort: true,
            width: '15'
        },
        {
            id: 'used',
            numeric: true,
            disablePadding: false,
            label: '',
            sort: true,
            width: '4'
        },
    ];


    const {t} = useTranslation();
    const [scheduleRows, setScheduleRows] = useState<ScheduleRow[]>([]);
    const [duplicatedRowsData, setDuplicatedRowsData] = useState<{
        destinationIndex: number | undefined,
        mediabankResources: MediabankResource[],
        duplicatedRows: ScheduleRow[]
        newRows: number[]
    }>(duplicatedRowsInitialData);

    const navigate = useNavigate();

    function placeNewRowsInVoohSchedule(res: ResourceMetaData[], mediabankResources: MediabankResource[], destinationIndex?: number) {
        const newScheduleRows: ScheduleRow[] = res.map((resourceMetaData, index) => {
            return {
                ...resourceMetaData,
                name: mediabankResources?.find(el => el.id === resourceMetaData.mediabankResourceId)?.name || '',
                uuid: getUuid(index),
                tagList: resourceMetaData.tagList,
                categoryColor: voohCategories?.filter(el => el.id === resourceMetaData.categoryId)[0]?.color || '',
                categoryName: voohCategories?.filter(el => el.id === resourceMetaData.categoryId)[0]?.name || '',
                voohItemType: VoohItemType.MediabankResource
            }
        })
        let tempData = Array.from(scheduleRows.filter(el => !el._destroy));
        tempData.splice(destinationIndex || 0, 0, ...newScheduleRows)
        setVoohItems(tempData.concat(Array.from(scheduleRows.filter(el => el._destroy))).map((el, index) => {
            return {
                ...el,
                position: index
            }
        }));
    }

    const handleDragEnd = (result: DropResult, visibleRows: MediabankResource[], mediabankResources: MediabankResource[], selectedMediabankFiles: number[]) => {

        try {
            let tempData = Array.from(scheduleRows);
            let previousItem = tempData[result.source.index - 1];
            let draggedItem = tempData[result.source.index];
            let nextDraggedItem = tempData[result.source.index + 1];
            const {source, destination} = result;
            if (destination?.droppableId === 'droppable-2' || !mediabankResources) {
                return
            }
            if (!result.destination) {
                return;
            }

            // Check if dragging from mediabank (droppable-2) and if it's a directory
            if (source.droppableId === 'droppable-2') {
                const draggedMediabankItem = visibleRows[result.source.index];
                if (draggedMediabankItem?.resourceType === 'dir') {
                    return; // Don't allow dragging directories
                }
            }

            if (source.droppableId === destination?.droppableId) {

                let [source_data] = tempData.splice(result.source.index, 1);
                tempData.splice(result.destination.index, 0, source_data);
                if (draggedItem.itemType === 'bumper' || draggedItem.voohItemType === 'bumper') {
                    tempData.splice(tempData.indexOf(nextDraggedItem), 1);
                    tempData.splice(tempData.indexOf(draggedItem) + 1, 0, nextDraggedItem);
                }
                if ((draggedItem.itemType === 'advertisement' || draggedItem.voohItemType === 'advertisement') && (previousItem.itemType === 'bumper' || previousItem.voohItemType === 'bumper')) {
                    tempData.splice(tempData.indexOf(previousItem), 1);
                    tempData.splice(tempData.indexOf(draggedItem), 0, previousItem);
                }

                setVoohItems(tempData);
            } else {
                if (result.draggableId === 'bumper' && selectedBumper) {
                    getFilesMeta([selectedBumper?.id]).then(async (res: ResourceMetaData[]) => {
                        const bumper = {
                            ...res[0],
                            uuid: getUuid(0),
                            name: selectedBumper.name,
                            voohItemType: VoohItemType.Bumper,
                        }
                        let tempData = Array.from(scheduleRows);
                        tempData.splice(destination?.index || 0, 0, bumper)
                        setVoohItems(tempData);
                        setRollbackAdvNumber(true)
                        setAdvertisingBlocksNumber(advertisingBlocksNumber + 1)

                    })
                    return
                }


                if (result.draggableId === 'advertisement') {
                    if (selectedBumper) {
                        if (tempData[result.destination.index - 1]?.voohItemType === 'bumper' && tempData[result.destination.index]?.voohItemType === 'advertisement') {
                            return;
                        }
                        getFilesMeta([selectedBumper?.id]).then(async (res: ResourceMetaData[]) => {
                            const bumper = {
                                ...res[0],
                                uuid: getUuid(0),
                                name: selectedBumper.name,
                                voohItemType: VoohItemType.Bumper,
                            }
                            let tempData = Array.from(scheduleRows);
                            tempData.splice(destination?.index || 0, 0, ...[bumper, {
                                name: 'REKLAMA',
                                voohItemType: VoohItemType.Advertisement,
                                uuid: getUuid(0),
                                duration: 0,
                                categoryId: null
                            }])
                            setVoohItems(tempData);
                            setRollbackAdvNumber(true)
                            setAdvertisingBlocksNumber(advertisingBlocksNumber + 1)
                        })
                        return;
                    } else {
                        let tempData = Array.from(scheduleRows);
                        tempData.splice(destination?.index || 0, 0, {
                            name: 'REKLAMA',
                            voohItemType: VoohItemType.Advertisement,
                            uuid: getUuid(0),
                            duration: 0,
                            categoryId: null
                        })
                        setVoohItems(tempData);
                        setRollbackAdvNumber(true)
                        setAdvertisingBlocksNumber(advertisingBlocksNumber + 1)
                        return;
                    }
                }

                const draggedItem = visibleRows[result.source.index];

                const newRows = selectedMediabankFiles.find(el => el === draggedItem.id) ? selectedMediabankFiles : [draggedItem.id];

                if (tempData[result.destination.index - 1]?.voohItemType === 'bumper' && tempData[result.destination.index]?.voohItemType === 'advertisement') {
                    return;
                }


                const alreadyExistingRows: ScheduleRow[] = []

                newRows.forEach((newRow) => {
                    const element = scheduleRows.find((row) => {
                        return row.mediabankResourceId === newRow
                    })
                    if (element) {
                        alreadyExistingRows.push(element)
                    }
                })
                if (alreadyExistingRows.length) {
                    setDuplicatedRowsData({
                        destinationIndex: destination?.index,
                        mediabankResources: mediabankResources,
                        duplicatedRows: alreadyExistingRows,
                        newRows: newRows
                    })
                } else {
                    getFilesMeta(newRows).then(async (res: ResourceMetaData[]) => {
                        placeNewRowsInVoohSchedule(res, mediabankResources, destination?.index);
                    })
                }


            }
        } catch {

        }


    };
    const dispatch = useDispatch();

    const theme = useTheme();
    const [checkedNodes, setCheckedNodes] = useState<string[]>([]);
    const [filteredNodes, setFilteredNodes] = useState([]);
    const [templateName, setTemplateName] = useState<string | null>(null);
    const [advertisingBlocksNumber, setAdvertisingBlocksNumber] = useState(DEFAULT_ADVERTISEMENTS);
    const [advertisingBlocksNumberPrevValue, setAdvertisingBlocksNumberPrevValue] = useState(DEFAULT_ADVERTISEMENTS);
    const [nodes, setNodes] = useState([]);
    const [treeState, setTreeState] = useState({key: Math.random()});
    const [openMediaTreeModal, setOpenMediaTreeModal] = useState(false);
    const [openUsedMediaTreeModal, setOpenUsedMediaTreeModal] = useState(false);

    const checkboxTreeRef = useRef<any>()
    const [filterNodesText, setFilterNodesText] = useState('')
    const [expanded, setExpanded] = useState([]);
    const [openWarningAfterChangingAdvNumber, setOpenWarningAfterChangingAdvNumber] = React.useState<boolean>(false);
    const [openSaveTemplateModal, setOpenSaveTemplateModal] = React.useState<boolean>(false);
    const [selectedTemplate, setSelectedTemplate] = useState<number | string>('')
    const [openRemoveTemplateConfirmation, setOpenRemoveTemplateConfirmation] = useState<number | null>(null);
    const [rollbackAdvNumber, setRollbackAdvNumber] = useState<boolean>(true);
    const [filterByIds, setFilterByIds] = useState<string | null>(null);
    const [fullScreen, setFullScreen] = useState<boolean>(false);
    const [openSaveScheduleTemplateModal, setOpenSaveScheduleTemplateModal] = useState<boolean>(false);
    const [scheduleTemplateName, setScheduleTemplateName] = useState<string>('');
    const [scheduleTemplates, setScheduleTemplates] = useState<any[]>([]);
    const [selectedScheduleTemplate, setSelectedScheduleTemplate] = useState<number | string>('');
    const [openRemoveScheduleTemplateConfirmation, setOpenRemoveScheduleTemplateConfirmation] = useState<number | null>(null);

    const [mediaLength, setMediaLength] = useState<number | null>(null);
    const voohForm = useSelector(selectVoohForm);

    const {isLoading, isError, isSuccess, data: mediaTemplatesData} =
        useGetMediaTemplatesQuery();
    const [createMediaTemplate, {isLoading: creatingMediaTemplate}] = useCreateMediaTemplateMutation()
    const [createVoohPlaylist, {isLoading: creatingVoohPlaylist}] = useCreateVoohPlaylistMutation()
    const [updateVoohPlaylist, {isLoading: updatingVoohPlaylist}] = useUpdateVoohPlaylistMutation()
    const [replanVooh] = useReplanVoohMutation()
    const [openReplanningConfirmation, setOpenReplanningConfirmation] = useState<boolean>(false);
    // One flag for both requests. The per-mutation isLoading flags drop between them, which would
    // blink the spinner in the middle of what is, for the operator, a single operation.
    const [replanningInProgress, setReplanningInProgress] = useState<boolean>(false);

    const [deleteMediaTemplate, {isLoading: deletingMediaTemplate}] = useDeleteMediaTemplateMutation()
    const [selectedBumper, setSelectedBumper] = React.useState<MediabankResource | null>(null);
    const [openSelectBumperDialog, setOpenSelectBumperDialog] = useState<boolean>(false);
    const [allMediaNodes, setAllMediaNodes] = useState<string[]>([])
    const [selectedSchedulesTag, setSelectedSchedulesTag] = useState<string | null>(null)
    const [sumSecSchedulesForSelectedTag, setSumSecSchedulesForSelectedTag] = useState<number>(0)

    const [getFile, {data: FileData}] = useLazyGetMediabankResourceQuery()
    let flattenNodeValues: string[] = []


    let checkedMediaOnFilterById: string[] = []

    const [formErrors, setFormErrors] = useState({
        name: false,
        startAt: false,
        endAt: false,
        nominalDuration: false,
    })
    const {id: urlId} = useParams()
    const [searchParams] = useSearchParams();
    const copyFromId = searchParams.get('copyFrom');

    const canCreateInternal = ability.can('create', 'VoohPlaylist');
    const canCreateExternal = ability.can('create', 'VoohPlaylistExternal');
    // Tabs to pick the type are only offered when creating a brand new playlist and
    // the user can create both kinds. In edit/copy mode the type comes from the source.
    const showTypeTabs = !urlId && !copyFromId && canCreateInternal && canCreateExternal;
    const isExternal = voohForm.playlistType === 'external';
    const voohResource: ResourceName = isExternal ? 'VoohPlaylistExternal' : 'VoohPlaylist';
    // Editing requires the "update" permission; creating/copying requires "create".
    const canSaveVooh = urlId ? ability.can('update', voohResource) : ability.can('create', voohResource);
    const canCopyVooh = ability.can('create', voohResource);

    const {isLoading: loadingMediaTree, data: mediaTree} =
        useGetMediaTreeQuery('MOVE_TV' || '');


    const [getFilesMetaData, {data: filesMetaData}] = useLazyGetResourcesMetaDataQuery()

    const {isLoading: loadingCmEnums, data: cmEnums} =
        useGetCmEnumsQuery();

    const {isLoading: loadingTags, data: tagsData} =
        useGetTagsQuery();

    const {isLoading: loadingVoohDetails, data: voohDetails} =
        useGetVoohPlaylistQuery(urlId || '', {skip: !urlId});

    const {isLoading: loadingCopyFromVoohDetails, data: copyFromVoohDetails} =
        useGetVoohPlaylistQuery(copyFromId || '', {skip: !copyFromId || !!urlId});


    const voohCategories: VoohCategory[] | undefined = cmEnums && cmEnums?.filter(el => el.name === 'categories')[0].values as VoohCategory[]


    useEffect(() => {
        // EDIT
        if (voohDetails && urlId) {
            const {voohItems, media, ownerships, ...rest} = voohDetails;
            const items = voohItems.map((voohItem, index) => {
                return {
                    ...voohItem,
                    position: voohItem.position,
                    tagList: voohItem.tagList,
                    id: voohItem.id,
                    uuid: getUuid(index),
                    voohItemType: voohItem.itemType,
                    name: voohItem.mediabankResourceId ? voohItem.resourceName : 'REKLAMA'
                }
            }).sort((a, b) => a.position - b.position)
            setScheduleRows(items);
            const loadedForm = {
                ...rest,
                voohItemsAttributes: voohItems.map((el) => {
                    return {
                        id: el.id,
                        mediabankResourceId: el.mediabankResourceId,
                        position: el.position,
                        tagList: el.tagList,
                        duration: el.duration,
                        categoryId: el.categoryId,
                        itemType: el.itemType
                    }
                }).sort((a, b) => a.position - b.position),
                localisablesMediatablesAttributes: media.map((mediaItem) => {
                    return {localisableId: mediaItem.id, localisableType: 'Medium'}
                }),
                ownershipsAttributes: ownerships
            };
            dispatch(setVoohForm(loadedForm))

            const bumper = voohItems.find(el => el.itemType === 'bumper')
            if (bumper) {
                getFile(bumper.id).unwrap().then(async (res) => {
                    setSelectedBumper({...res.data})
                })
            }
            setAdvertisingBlocksNumber(voohItems.filter(el => !el.mediabankResourceId).length);
            setRollbackAdvNumber(true)
            return;
        }

        // COPY (create-mode, but prefilled)
        if (copyFromId) {
            if (copyFromVoohDetails) {
                const {voohItems, media, ownerships, ...rest} = copyFromVoohDetails;
                const items = voohItems.map((voohItem, index) => {
                    return {
                        ...voohItem,
                        position: voohItem.position,
                        tagList: voohItem.tagList,
                        uuid: getUuid(index),
                        voohItemType: voohItem.itemType,
                        name: voohItem.mediabankResourceId ? voohItem.resourceName : 'REKLAMA'
                    }
                }).sort((a, b) => a.position - b.position)
                setScheduleRows(items);

                dispatch(setVoohForm({
                    ...rest,
                    id: null,
                    name: `${rest.name} copy`,
                    startAt: '',
                    endAt: '',
                    defaultPlaylist: false,
                    photographic: false,
                    replanning: false,
                    voohItemsAttributes: voohItems.map((el) => {
                        return {
                            mediabankResourceId: el.mediabankResourceId,
                            position: el.position,
                            tagList: el.tagList,
                            duration: el.duration,
                            categoryId: el.categoryId,
                            itemType: el.itemType,
                            bufor: el.bufor
                        }
                    }).sort((a, b) => a.position - b.position),
                    localisablesMediatablesAttributes: media.map((mediaItem) => {
                        return {localisableId: mediaItem.id, localisableType: 'Medium'}
                    }),
                    ownershipsAttributes: ownerships.map(({id, ...rest}) => rest)
                }))

                const bumper = voohItems.find(el => el.itemType === 'bumper')
                if (bumper?.mediabankResourceId) {
                    getFile(bumper.mediabankResourceId).unwrap().then(async (res) => {
                        setSelectedBumper({...res.data})
                    })
                }

                setAdvertisingBlocksNumber(voohItems.filter(el => !el.mediabankResourceId).length);
                setRollbackAdvNumber(true)
            } else {
                // While copy source loads, keep predictable "new" defaults
                dispatch(setVoohForm(initialVoohFormState.voohForm));
                setAdvertisingBlocksNumber(DEFAULT_ADVERTISEMENTS)
                setRollbackAdvNumber(false)
                const advertisements: ScheduleRow[] = [...Array(DEFAULT_ADVERTISEMENTS).keys()].map((_, index) => {
                    return {
                        name: 'REKLAMA',
                        uuid: getUuid(index),
                        voohItemType: VoohItemType.Advertisement,
                        duration: 0,
                        categoryId: null
                    }
                })
                setScheduleRows(advertisements)
                setFilterByIds(null)
            }
            return;
        }

        // NEW
        const newPlaylistType: VoohPlaylistType = canCreateInternal ? 'internal' : 'external';
        dispatch(setVoohForm({...initialVoohFormState.voohForm, playlistType: newPlaylistType}));
        setAdvertisingBlocksNumber(DEFAULT_ADVERTISEMENTS)
        setRollbackAdvNumber(false)
        if (newPlaylistType === 'external') {
            // External playlists have no advertisement blocks; start with an empty schedule.
            setScheduleRows([])
        } else {
            const advertisements: ScheduleRow[] = [...Array(DEFAULT_ADVERTISEMENTS).keys()].map((_, index) => {
                return {
                    name: 'REKLAMA',
                    uuid: getUuid(index),
                    voohItemType: VoohItemType.Advertisement,
                    duration: 0,
                    categoryId: null
                }
            })
            setScheduleRows(advertisements)
        }
        setFilterByIds(null)

    }, [voohDetails, urlId, copyFromId, copyFromVoohDetails])

    useEffect(() => {

        const sumSec = scheduleRows
            .filter(el => el.tagList?.find(tag => tag === selectedSchedulesTag))
            ?.reduce((sum, current) => sum + (current?.duration || 0), 0)!

        setSumSecSchedulesForSelectedTag(sumSec)
    }, [scheduleRows, selectedSchedulesTag])

    useEffect(() => {
        // Load schedule templates from localStorage
        const loadScheduleTemplates = () => {
            try {
                const templatesData = localStorage.getItem('scheduleTemplates');
                if (templatesData) {
                    const templates = JSON.parse(templatesData);
                    setScheduleTemplates(templates);
                }
            } catch (err) {
                console.error('Error loading schedule templates:', err);
            }
        };
        loadScheduleTemplates();
    }, [openSaveScheduleTemplateModal])


    const setVoohItems = (scheduleRows: ScheduleRow[], destroyedElement?: ScheduleRow, skipClearTemplate?: boolean) => {
        // Clear template selection when items are modified (unless we're loading a template)
        if (!skipClearTemplate) {
            setSelectedScheduleTemplate('');
        }

        if (destroyedElement?.id && voohDetails) {

            let destroyedElements: VoohItem[] = []
            let elements: VoohItem[] = []

            const elementsWithDestroy = voohForm.voohItemsAttributes.forEach((el, index) => {
                if (el.id === destroyedElement.id) {
                    destroyedElements.push({...el, _destroy: true})
                } else {
                    elements.push(el)
                }
            })

            dispatch(setVoohForm({
                ...voohForm,
                voohItemsAttributes: [...elements, ...destroyedElements].map((el, index) => {
                    return {...el, position: index}
                }),
            }))

            let destroyedScheduleRows: ScheduleRow[] = []
            let existingScheduleRows: ScheduleRow[] = []
            scheduleRows.forEach((el) => {
                if (destroyedElement?.id === el.id) {
                    destroyedScheduleRows.push({...el, _destroy: true})
                } else {
                    existingScheduleRows.push(el)
                }
            })
            setScheduleRows([...existingScheduleRows, ...destroyedScheduleRows].map((el, index) => {
                return {
                    ...el,
                    position: index,
                }
            }));
            return;
        }


        if (destroyedElement) {
            const voohItems: VoohItem[] = scheduleRows.filter(el => el.uuid !== destroyedElement.uuid).map((element, position) => {
                let voohItem: VoohItem = {
                    mediabankResourceId: element.mediabankResourceId,
                    position,
                    duration: element.duration,
                    tagList: element.tagList,
                    categoryId: element.categoryId,
                    itemType: element.voohItemType,
                    bufor: element.bufor
                }
                voohItem = element._destroy ? {...voohItem, _destroy: element._destroy} : {...voohItem}

                return element.id ? {id: element.id, ...voohItem} : {...voohItem}
            })

            dispatch(setVoohForm({
                ...voohForm,
                voohItemsAttributes: voohItems,
            }));
            setScheduleRows(scheduleRows.filter(el => el.uuid !== destroyedElement.uuid));
            return;
        }

        const voohItems: VoohItem[] = scheduleRows.sort(el => el._destroy ? 1 : -1).map((element, position) => {
            let voohItem: any = {
                mediabankResourceId: element.mediabankResourceId,
                position,
                duration: element.duration,
                categoryId: element.categoryId,
                itemType: element.voohItemType,
                bufor: element.bufor,
                _destroy: element._destroy,
            }


            return element.id ? {id: element.id, ...voohItem, position: position} : {...voohItem, position: position}
        })


        dispatch(setVoohForm({
            ...voohForm,
            voohItemsAttributes: voohItems.sort(el => el._destroy ? 1 : -1),
        }));
        setScheduleRows(scheduleRows.sort(el => el._destroy ? 1 : -1));
    }


    let mediaSize = 0

    const onSelectMediaById = (event: any) => {
        setFilterByIds(event.target.value)
    }

    const createTemplate = async () => {
        if (!templateName) {
            return;
        }

        try {
            const payload = {
                name: templateName,
                localisablesMediatablesAttributes: checkedNodes.map((el) => {
                    return {
                        localisableId: parseInt(el.split('_')[0]),
                        localisableType: el.split('_')[1].replace(/[^A-Za-z]+/g, '')
                    }

                })
            }
            const result = await createMediaTemplate(payload).unwrap()
            setOpenSaveTemplateModal(false);
            setTemplateName(null)
        } catch (err: any) {
            if (err?.status && err.status === 422) {
                enqueueSnackbar('Wystąpił błąd - nazwa już istnieje w bazie, proszę wybrać inną', {variant: 'error'})
            } else if (err?.data?.errors?.default_playlist) {
                enqueueSnackbar('Wystąpił błąd - domyślna ramówka już istnieje', {variant: 'error'});
            } else {
                enqueueSnackbar('Wystąpił błąd', {variant: 'error'});
            }
            setOpenSaveTemplateModal(false);
            setTemplateName(null)
        }
    }

    const removeTemplate = async (templateId: number) => {
        try {
            const result = await deleteMediaTemplate(templateId).unwrap()
            setSelectedTemplate('')
        } catch (err: any) {
            enqueueSnackbar('Wystąpił błąd', {variant: 'error'});
        }
    }

    const filterNodesByIds = (filtered: any, node: any) => {
        const filterByIdsArr = (filterByIds || '').split(/,| |;/)
        const nodeId = node.label.split('-')[0].trim()
        if (
            // Node's label matches the search string
            filterByIdsArr.some(filteredNodeId => filteredNodeId === nodeId)
        ) {
            if (node.value.includes('_Medium')) {
                checkedMediaOnFilterById.push(node.value)
            }

            filtered.push(node)
            return filtered;
        }

        if (node.children && node.children.length > 0) {
            const children = (node.children || []).reduce(filterNodesByIds, []);
            if (children.length > 0) {
                filtered.push({...node, children});
            }
        }
        return filtered;
    }

    function getSelectedMediaArrayFromTree(entireObj: MediaNode[]) {
        let foundArr: MediaNode[] = [];
        let building = '';
        JSON.stringify(entireObj, (_, nestedValue) => {
            if (!nestedValue) {
                console.warn(`Warning: Null value for Medium object key - ${_}`)
                return;
            }

            if (nestedValue.type === 'Media::Building') {
                building = nestedValue.name;
            }
            if (checkedNodes.some(el => el.split('_')[0] === nestedValue.id?.toString())) {
                foundArr.push({...nestedValue, building});
            }
            return nestedValue;
        });
        return foundArr;
    };


    const onFilterNodeChange = (event: any) => {
        setFilterNodesText(event.target.value)
    }
    const filterNodes = (filtered: any, node: any) => {
        if (
            // Node's label matches the search string
            node.label.toLocaleLowerCase().match(filterNodesText.toString().toLocaleLowerCase())
        ) {
            filtered.push(node)
            return filtered;
        }

        if (node.children && node.children.length > 0) {
            const children = (node.children || []).reduce(filterNodes, []);
            if (children.length > 0) {
                filtered.push({...node, children});
            }
        }
        return filtered;
    }

    const buildMediaTreeCheckboxNodes: any = (mediaTree: MediaNode[]) => {
        return mediaTree.map((element: MediaNode, index) => {
            if (element.children && element.children.length > 0) {
                let val = element.id + '_' + element.type + getUuid(index)
                let place = '';

                if (element.type === 'Media::Agglomeration') {
                    place = '(Aglomeracja)'
                } else if (element.type === 'Media::City') {
                    place = '(Miasto)'
                }

                return {
                    value: val,
                    label: element.name + ' ' + `${place}`,
                    children: buildMediaTreeCheckboxNodes(element.children),
                }
            } else {
                let val = element.id + '_' + element.type + getUuid(index)
                flattenNodeValues.push(val)
                mediaSize += 1;
                return {
                    value: val,
                    label: element.asId ? element.asId + ' - ' + element.name : element.name,
                }
            }
        })
    }

    useEffect(() => {
        if (mediaTree) {
            mediaSize = 0;
            const mediaTreeCheckboxNodes = buildMediaTreeCheckboxNodes(mediaTree)
            setAllMediaNodes(flattenNodeValues)
            setTimeout(() => {
                setNodes(mediaTreeCheckboxNodes)
                setFilteredNodes([{
                    label: 'Wszystkie nośniki',
                    value: 'all',
                    children: mediaTreeCheckboxNodes
                }] as any)
                setMediaLength(mediaSize)
            }, 200)

            const detailsForMedia = urlId ? voohDetails : (copyFromId ? copyFromVoohDetails : undefined);
            if (detailsForMedia) {
                setCheckedNodes(flattenNodeValues.filter(el => detailsForMedia.media.some(media => media.id.toString() === el.split('_')[0])))
            }

        }


    }, [mediaTree, voohDetails, copyFromVoohDetails, urlId, copyFromId]);


    const updateMediaInForm = (checkedNodes: string[]) => {
        const checkedMedia = checkedNodes.filter(el => el !== 'all').map((node: string) => {
            return node.split('_')[0]
        })

        const mediaToAdd = checkedMedia.map((checkedMediaElement) => {
            return {localisableId: checkedMediaElement, localisableType: 'Medium'}
        })
        dispatch(setVoohForm({
            ...voohForm,
            localisablesMediatablesAttributes: [...mediaToAdd as []]
        }));
    }


    useDebounce(() => {
            // Reset nodes back to unfiltered state
            if (!filterNodesText) {
                setFilteredNodes([{
                    label: 'Wszystkie nośniki',
                    value: 'all',
                    children: nodes
                }] as any);
                return;
            }
            setFilterByIds('')
            setFilteredNodes(
                [{
                    label: 'Wszystkie nośniki',
                    value: 'all',
                    children: nodes.reduce(filterNodes, [])
                }] as any)
            checkboxTreeRef.current.onExpandAll()
        }, [filterNodesText], 100
    );

    useDebounce(() => {
            if (filterByIds !== null) {
                checkedMediaOnFilterById = []
                if (filterByIds === '') {
                    setCheckedNodes([])
                }
                nodes.reduce(filterNodesByIds, [])
                setCheckedNodes(checkedMediaOnFilterById)
                updateMediaInForm(checkedMediaOnFilterById)
            }
        }, [filterByIds], 100
    );

    useDebounce(() => {
            if (voohForm.playlistType === 'external') {
                return;
            }
            const customScheduleRows = scheduleRows.find(el => el.voohItemType !== VoohItemType.Advertisement)
            if (customScheduleRows && advertisingBlocksNumber >= MIN_ADVERTISEMENTS && !rollbackAdvNumber) {
                setOpenWarningAfterChangingAdvNumber(true)
            } else {
                setAdvertisingBlocksNumberPrevValue(advertisingBlocksNumber)
                if (!rollbackAdvNumber && advertisingBlocksNumber >= MIN_ADVERTISEMENTS) {
                    const advertisements: ScheduleRow[] = [...Array(advertisingBlocksNumber).keys()].map((_, index) => {
                        return {
                            name: 'REKLAMA',
                            uuid: getUuid(index),
                            voohItemType: VoohItemType.Advertisement,
                            duration: 0,
                            categoryId: null
                        }
                    })
                    setVoohItems(advertisements)
                    setSelectedBumper(null)
                }
                setRollbackAdvNumber(false)
            }
        }, [advertisingBlocksNumber], 800
    );

    async function getFilesMeta(ids: number[]) {
        let query = ids.map((id) => {
            return `mediabank_resource_ids[]=${id}`
        }).join('&');
        return getFilesMetaData(query).unwrap()
    };


    const ITEM_HEIGHT = 48;
    const ITEM_PADDING_TOP = 8;
    const MenuProps = {
        PaperProps: {
            style: {
                maxHeight: ITEM_HEIGHT * 4.5 + ITEM_PADDING_TOP,
                width: 250,
            },
        },
    };

    function getStyles() {
        return {
            fontWeight: theme.typography.fontWeightMedium
        };
    }


    const handleOpenNodesModal = () => {
        setOpenMediaTreeModal(true)
    };
    const handleCloseNodesModal = () => {
        setOpenMediaTreeModal(false)
    };

    const handleSelectTemplate = (templateId: number, flattenNodes: string[]) => {
        setSelectedTemplate(templateId);
        const selectedTemplate = mediaTemplatesData!.find(template => template.id === templateId);
        const media = selectedTemplate!.media.map(el => el + '_Medium')
        setCheckedNodes(allMediaNodes.filter(el => media.some(media => media.split('_')[0] === el.split('_')[0])))
        updateMediaInForm(media);
    }

    const addAuthorizedPerson = (principalId: number, authorizedPersonRights: string, principalType: 'User' | 'Group') => {
        dispatch(setVoohForm({
            ...voohForm,
            ownershipsAttributes: [...voohForm.ownershipsAttributes.filter(
                person => !(person.principalType === principalType && person.principalId === principalId)
            ), {
                principalType,
                principalId,
                accessLevel: authorizedPersonRights
            }]
        }));
    }

    function openImageFile(mediabankResource: MediabankResource) {
        if (mediabankResource.resourceFileUrl) {
            openLink(mediabankResource.resourceFileUrl)
        } else {
            getFile(mediabankResource.id).unwrap().then(async (res) => {
                if (res.data.resourceFileUrl) {
                    openLink(res.data.resourceFileUrl);
                }
            })
        }
    };

    function openLink(url: string) {
        const link = document.createElement("a");
        link.href = url
        link.target = '_blank'
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link)
    }

    const saveScheduleTemplate = () => {
        if (!scheduleTemplateName.trim()) {
            return;
        }

        try {
            // Get existing templates from localStorage
            const existingTemplates = localStorage.getItem('scheduleTemplates');
            const templates = existingTemplates ? JSON.parse(existingTemplates) : [];

            // Create new template object
            const newTemplate = {
                id: Date.now(), // Simple unique ID
                name: scheduleTemplateName,
                scheduleRows: scheduleRows.filter(row => !row._destroy), // Save only non-destroyed rows
                createdAt: new Date().toISOString()
            };

            // Add new template to array
            templates.push(newTemplate);

            // Save back to localStorage
            localStorage.setItem('scheduleTemplates', JSON.stringify(templates));

            // Update local state
            setScheduleTemplates(templates);

            enqueueSnackbar('Szablon ramówek został zapisany', {variant: 'success'});
            setOpenSaveScheduleTemplateModal(false);
            setScheduleTemplateName('');
        } catch (err) {
            enqueueSnackbar('Wystąpił błąd podczas zapisywania szablonu', {variant: 'error'});
        }
    };

    const loadScheduleTemplate = (templateId: number) => {
        try {
            const template = scheduleTemplates.find(t => t.id === templateId);
            if (!template) {
                enqueueSnackbar('Nie znaleziono szablonu', {variant: 'error'});
                return;
            }

            // Map the saved schedule rows and regenerate UUIDs, but remove any id/mediabankResourceId references
            const loadedRows: ScheduleRow[] = template.scheduleRows.map((row: any, index: number) => {
                const {id, ...rowWithoutId} = row; // Remove id from saved template
                return {
                    ...rowWithoutId,
                    uuid: getUuid(index), // Generate new UUID for each row
                };
            });

            // If editing existing vooh playlist, mark all existing items as _destroy: true
            let destroyedItems: VoohItem[] = [];
            if (urlId && voohForm.voohItemsAttributes.length > 0) {
                destroyedItems = voohForm.voohItemsAttributes
                    .filter(item => item.id) // Only items that exist in DB
                    .map((item) => ({
                        ...item,
                        _destroy: true
                    }));
            }

            // Create new vooh items from template (without IDs as they're new items)
            const newVoohItems: VoohItem[] = loadedRows.map((element, position) => {
                return {
                    mediabankResourceId: element.mediabankResourceId,
                    position,
                    duration: element.duration,
                    tagList: element.tagList,
                    categoryId: element.categoryId,
                    itemType: element.voohItemType,
                    bufor: element.bufor
                };
            });

            // Combine: new items first, then destroyed items at the end
            const combinedVoohItems = [...newVoohItems, ...destroyedItems].map((el, index) => ({
                ...el,
                position: index
            }));

            dispatch(setVoohForm({
                ...voohForm,
                voohItemsAttributes: combinedVoohItems,
            }));

            // For scheduleRows, if editing existing, add destroyed rows with _destroy flag
            let destroyedScheduleRows: ScheduleRow[] = [];
            if (urlId && scheduleRows.length > 0) {
                destroyedScheduleRows = scheduleRows
                    .filter(row => row.id) // Only rows that exist in DB
                    .map((row) => ({
                        ...row,
                        _destroy: true
                    }));
            }

            setScheduleRows([...loadedRows, ...destroyedScheduleRows]);
            setSelectedScheduleTemplate(templateId);

            // Update advertising blocks number based on loaded template
            const advCount = loadedRows.filter(row => row.voohItemType === VoohItemType.Advertisement).length;
            if (advCount > 0) {
                setAdvertisingBlocksNumber(advCount);
                setRollbackAdvNumber(true);
            }

            enqueueSnackbar('Szablon ramówek został załadowany', {variant: 'success'});
        } catch (err) {
            console.error('Error loading schedule template:', err);
            enqueueSnackbar('Wystąpił błąd podczas ładowania szablonu', {variant: 'error'});
        }
    };

    const deleteScheduleTemplate = (templateId: number) => {
        try {
            // Filter out the template to delete
            const updatedTemplates = scheduleTemplates.filter(t => t.id !== templateId);

            // Save back to localStorage
            localStorage.setItem('scheduleTemplates', JSON.stringify(updatedTemplates));

            // Update local state
            setScheduleTemplates(updatedTemplates);

            // Clear selection if the deleted template was selected
            if (selectedScheduleTemplate === templateId) {
                setSelectedScheduleTemplate('');
            }

            enqueueSnackbar('Szablon ramówek został usunięty', {variant: 'success'});
        } catch (err) {
            console.error('Error deleting schedule template:', err);
            enqueueSnackbar('Wystąpił błąd podczas usuwania szablonu', {variant: 'error'});
        }
    };

    const validateForm = (): boolean => {
        const invalidNominalDuration = !isExternal && (voohForm.nominalDuration < MIN_NOMINAL_DURATION || !voohForm.nominalDuration);
        setFormErrors({
            name: !voohForm.name.trim(),
            endAt: voohForm.defaultPlaylist ? false : !voohForm.endAt,
            startAt: voohForm.defaultPlaylist ? false : !voohForm.startAt,
            nominalDuration: invalidNominalDuration,
        })

        return !(!voohForm.name || (voohForm.defaultPlaylist ? false : !voohForm.endAt) || (voohForm.defaultPlaylist ? false : !voohForm.startAt) ||
            invalidNominalDuration);
    }

    const saveReplanning = () => {
        if (!validateForm()) {
            enqueueSnackbar('Formularz zawiera błędy', {variant: 'error'});
            return;
        }
        if (!voohForm.localisablesMediatablesAttributes?.length) {
            enqueueSnackbar('Aby replanować ramówkę, wybierz przynajmniej jeden nośnik', {variant: 'error'});
            return;
        }
        setOpenReplanningConfirmation(true);
    }

    // Both requests are one operation for the operator: one spinner, one outcome. The playlist has
    // to be persisted before the planner is asked for anything - it reads the playlist back from
    // the backend, so replanning first would plan against the state from before the edit.
    const confirmReplanning = async () => {
        const voohItems = voohForm.voohItemsAttributes.map((el, index) => {
            return {
                ...el,
                position: index
            }
        })

        setReplanningInProgress(true);

        try {
            await updateVoohPlaylist({...voohForm, voohItemsAttributes: voohItems}).unwrap()
            dispatch(baseApi.util.invalidateTags([ApiTags.VoohDetails]));
            await replanVooh().unwrap()
            enqueueSnackbar('Pomyślnie zreplanowano ramówkę', {variant: 'success'});
        } catch (err: any) {
            if (err?.data?.errors?.default_playlist) {
                enqueueSnackbar('Wystąpił błąd - domyślna ramówka już istnieje', {variant: 'error'});
            } else if (err?.data?.errors?.media?.[0]) {
                enqueueSnackbar(<><p>Wystąpił błąd. Konflikt zakresu dat i nośników z ramówką: &nbsp;</p>
                    <Link href={`/emissions/vooh-list/${err?.data?.errors?.media[0].details.playlist_id}`}
                          className="link" style={{color: 'white'}}>
                        <strong>{err?.data?.errors?.media[0].details.playlist_name}</strong>
                    </Link>
                </>, {variant: 'error', autoHideDuration: 15000});
            } else {
                enqueueSnackbar('Wystąpił błąd podczas replanowania ramówki', {variant: 'error'});
            }
        } finally {
            setReplanningInProgress(false);
            setOpenReplanningConfirmation(false);
        }
    }

    const saveForm = async () => {
        if (!validateForm()) {
            enqueueSnackbar('Formularz zawiera błędy', {variant: 'error'});
            return;
        } else {
            try {
                const voohItems = voohForm.voohItemsAttributes.map((el, index) => {
                    return {
                        ...el,
                        position: index
                    }
                })
                if (urlId) {

                    // const updatedForm = {
                    //     ...voohForm,
                    //     voohItemsAttributes: voohForm.voohItemsAttributes.filter(el => !voohDetails?.voohItems.some(item => item.id === el.id)),
                    // }
                    await updateVoohPlaylist({...voohForm, voohItemsAttributes: voohItems}).unwrap()
                    dispatch(baseApi.util.invalidateTags([ApiTags.VoohDetails]));

                } else {
                    await createVoohPlaylist({...voohForm, voohItemsAttributes: voohItems}).unwrap()
                }

                navigate('/emissions/vooh-list')
                enqueueSnackbar(`Pomyślnie ${urlId ? 'zaktualizowano' : 'utworzono'} ramówke`, {variant: 'success'});
                setVoohForm(initialVoohFormState.voohForm)
            } catch (err: any) {
                console.log(err)
                if (err?.data?.errors?.default_playlist) {
                    enqueueSnackbar('Wystąpił błąd - domyślna ramówka już istnieje', {variant: 'error'});
                } else if (err?.data?.errors?.media[0]) {
                    enqueueSnackbar(<><p>Wystąpił błąd. Konflikt zakresu dat i nośników z ramówką: &nbsp;</p>
                        <Link href={`/emissions/vooh-list/${err?.data?.errors?.media[0].details.playlist_id}`}
                              className="link" style={{color: 'white'}}>
                            <strong>{err?.data?.errors?.media[0].details.playlist_name}</strong>
                        </Link>
                    </>, {variant: 'error', autoHideDuration: 15000});
                } else {
                    enqueueSnackbar('Wystąpił błąd', {variant: 'error'});
                }
            }
        }

    }
    const handleChangePlaylistType = (newType: VoohPlaylistType) => {
        if (newType === voohForm.playlistType) {
            return;
        }
        setSelectedBumper(null);
        setRollbackAdvNumber(true);
        setAdvertisingBlocksNumber(DEFAULT_ADVERTISEMENTS);
        if (newType === 'external') {
            setScheduleRows([]);
            dispatch(setVoohForm({
                ...voohForm,
                playlistType: newType,
                photographic: false,
                replanning: false,
                voohItemsAttributes: [],
            }));
        } else {
            const advertisements: ScheduleRow[] = [...Array(DEFAULT_ADVERTISEMENTS).keys()].map((_, index) => ({
                name: 'REKLAMA',
                uuid: getUuid(index),
                voohItemType: VoohItemType.Advertisement,
                duration: 0,
                categoryId: null
            }));
            setScheduleRows(advertisements);
            dispatch(setVoohForm({
                ...voohForm,
                playlistType: newType,
                voohItemsAttributes: advertisements.map((el, position) => ({
                    position,
                    duration: el.duration,
                    categoryId: el.categoryId,
                    itemType: el.voohItemType,
                })),
            }));
        }
    };


    return <>
        <Box className={'VoohForm'}>
            <Box className={'page-header'} sx={{paddingBottom: '20px'}}>
                <Box sx={{display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', gap: '60px', marginTop: '50px'}}>
                    <Box sx={{display: 'flex', alignItems: 'center', gap: '20px'}}>
                        <h1 style={{margin: 0}}>{t('vooh.scheduleVOOH')}</h1>
                        {urlId && canSaveVooh && (
                            <Button
                                variant="contained"
                                sx={{paddingX: 3}}
                                disabled={updatingVoohPlaylist || replanningInProgress}
                                onClick={saveReplanning}
                            >
                                Replanowanie
                                {replanningInProgress &&
                                    <CircularProgress sx={{marginLeft: '10px'}} size={20} color={'inherit'}/>}
                            </Button>
                        )}
                    </Box>
                    <Box sx={{display: 'flex', alignItems: 'center', gap: '12px'}}>
                        {urlId && canCopyVooh && (
                            <Button variant="contained" sx={{paddingX: 3, marginX: 3}} onClick={() => navigate(`/emissions/new-schedule?copyFrom=${urlId}`)}>
                                Kopiuj
                            </Button>
                        )}
                        <FormGroup sx={{display: 'flex', flexDirection: 'row', gap: '12px'}}>
                            {!isExternal && (<FormControlLabel control={<Switch checked={voohForm.defaultPlaylist} onChange={(event) => {
                                dispatch(setVoohForm({
                                    ...voohForm,
                                    defaultPlaylist: event.target.checked,
                                    photographic: event.target.checked ? false : voohForm.photographic
                                }))
                            }}/>} label="Domyślna ramówka"/>)}
                            {!isExternal && (<FormControlLabel control={<Switch checked={voohForm.photographic} onChange={(event) => {
                                dispatch(setVoohForm({
                                    ...voohForm,
                                    photographic: event.target.checked,
                                    defaultPlaylist: event.target.checked ? false : voohForm.defaultPlaylist
                                }))
                            }}/>} label="Ramówka fotograficzna"/>)}
                        </FormGroup>
                    </Box>
                </Box>
                <p>Uzupenij parametry ramówki</p>
            </Box>
        </Box>
        {showTypeTabs && (
            <Box sx={{borderBottom: 1, borderColor: 'divider', marginBottom: '20px'}}>
                <Tabs
                    value={voohForm.playlistType}
                    onChange={(_, value) => handleChangePlaylistType(value as VoohPlaylistType)}
                >
                    <Tab label="Wewnętrzna" value="internal"/>
                    <Tab label="Zewnętrzna" value="external"/>
                </Tabs>
            </Box>
        )}
        <form autoComplete="off">

            <TextField
                value={voohForm.name}
                label="Nazwa ramówki"
                onChange={(event) => {
                    setFormErrors({...formErrors, name: !event.target.value.trim()})
                    dispatch(setVoohForm({
                        ...voohForm,
                        name: event.target.value
                    }));
                }}
                required
                variant="outlined"
                sx={{mb: 3}}
                fullWidth
                error={formErrors.name}/>

            <FormControl fullWidth sx={{marginTop: '15px', paddingBottom: voohForm.defaultPlaylist ? '0' : '20px'}}>
                <InputLabel shrink id="demo-simple-select-label">Format nośnika</InputLabel>
                <Select
                    variant={"outlined"}
                    labelId="demo-simple-select-label"
                    id="demo-simple-select"
                    label="Format nośnika"
                    value={voohForm.mediaFormat || ''}
                    notched
                    onChange={(event) => {
                        dispatch(setVoohForm({
                            ...voohForm,
                            mediaFormat: event.target.value
                        }));
                    }}
                >
                    <MenuItem
                        key={'MOVE_TV'}
                        value={'MOVE_TV'}>Vooh</MenuItem>
                </Select>
            </FormControl>


            {!voohForm.defaultPlaylist && (<><Button disabled={loadingMediaTree} variant="contained"
                                                     onClick={handleOpenNodesModal} sx={{marginTop: '20px'}}> Wybór
                nośników &nbsp;
                {(mediaTree?.length === 0) ? <CircularProgress sx={{marginLeft: '10px'}}
                                                               size={20}/> : `${checkedNodes.length}/${mediaLength || 0}`}</Button>

                <Button disabled={loadingMediaTree} variant="text"
                        onClick={() => {
                            setOpenUsedMediaTreeModal(true)
                            if (mediaTree) {
                                console.log(getSelectedMediaArrayFromTree(mediaTree))
                            }

                        }} sx={{marginTop: '20px', marginLeft: '20px'}}>Wybrane
                    nośniki {(loadingMediaTree) && voohForm.mediaFormat ?
                        <CircularProgress sx={{marginLeft: '10px'}}
                                          size={20}/> :
                        <strong>&nbsp;{`${checkedNodes.length}/${mediaLength || 0}`} </strong>
                    }</Button></>)
            }

            {!voohForm.defaultPlaylist && (
                <Box sx={{display: 'flex', gap: '40px', alignItems: 'center', paddingTop: '40px'}}>
                    <Box sx={{width: '100%'}}>
                        <DatePicker
                            minDate={dayjs(new Date())}
                            maxDate={voohForm.endAt ? dayjs(new Date(voohForm.endAt)) : undefined}
                            value={dayjs(voohForm.startAt)}
                            slotProps={{
                                textField: {
                                    fullWidth: true,
                                    variant: 'outlined',
                                    error: formErrors.startAt
                                },
                            }}
                            label={t('creations.steps.fillInCreationParameters.emissionStart')}
                            onChange={(event) => {
                                setFormErrors({...formErrors, startAt: false})
                                dispatch(setVoohForm({
                                    ...voohForm,
                                    startAt: dayjs(event).date() ? dayjs(event).set('hour', 0).set('minute', 0).format() : ''
                                }))
                            }}/></Box>
                    <span>-</span>
                    <Box sx={{width: '100%'}}>
                        <DatePicker
                            minDate={voohForm.startAt ? dayjs(voohForm.startAt) : dayjs(new Date())}
                            value={dayjs(voohForm.endAt)}
                            slotProps={{
                                textField: {
                                    fullWidth: true,
                                    variant: 'outlined',
                                    error: formErrors.endAt
                                },
                            }}
                            label={t('creations.steps.fillInCreationParameters.emissionEnd')}
                            onChange={(event) => {
                                setFormErrors({...formErrors, endAt: false})
                                dispatch(setVoohForm({
                                    ...voohForm,
                                    endAt: dayjs(event).date() ? dayjs(event).set('hour', 23).set('minute', 59).format() : ''
                                }))
                            }}/></Box>
                </Box>)}

            <Dialog
                open={openMediaTreeModal}
                onClose={handleCloseNodesModal}
                fullWidth={true}
                maxWidth="md"
            >

                <DialogTitle id="alert-dialog-title">
                    {t("draftForm.chooseMedia")}
                </DialogTitle>
                <DialogContent>

                    {(loadingMediaTree) && (
                        <Box sx={{display: 'flex', justifyContent: 'center', alignItems: 'center', height: '300px'}}>
                            <CircularProgress size={100}/>
                        </Box>)}

                    <Box sx={{minHeight: '300px'}}>
                        {mediaTree && (
                            <><TextField
                                value={filterNodesText}
                                label="Wyszukaj..."
                                onInput={onFilterNodeChange}
                                variant="outlined"
                                sx={{mt: 1, mb: 3}}
                                fullWidth
                            />
                                <TextField
                                    value={filterByIds}
                                    label="Zaznacz nośniki podając ID klubów"
                                    onInput={onSelectMediaById}
                                    helperText={"Dozwolone separatory: spacja, średnik, przecinek"}
                                    variant="outlined"
                                    sx={{mt: 1, mb: 3}}
                                    fullWidth
                                />

                                <FormControl fullWidth sx={{marginBottom: '30px'}}>
                                    <InputLabel shrink id="demo-simple-select-label">Szablony</InputLabel>
                                    <Select
                                        size="small"
                                        variant={"outlined"}
                                        labelId="demo-simple-select-label"
                                        id="demo-simple-select"
                                        label="Szablony"
                                        value={selectedTemplate}
                                        renderValue={(selected) => (
                                            <span>{mediaTemplatesData && mediaTemplatesData.filter(el => el.id === selected)[0].name}</span>
                                        )}
                                        notched
                                    >
                                        {mediaTemplatesData?.length === 0 && (<MenuItem value="" onClick={() => {
                                            setSelectedTemplate('')
                                        }}>
                                            <em>Brak szablonów</em>
                                        </MenuItem>)}
                                        {mediaTemplatesData && mediaTemplatesData.map(template => (

                                            <MenuItem value={template.id} onClick={() => {
                                                handleSelectTemplate(template.id, flattenNodeValues)
                                            }} key={template.id}>
                                                <Box sx={{
                                                    display: 'flex',
                                                    justifyContent: 'space-between',
                                                    alignItems: 'center',
                                                    width: '100%'
                                                }}>
                                                    <span>{template.name}</span>
                                                    <IconButton color={'error'} edge="end" aria-label="remove"
                                                                onClick={(event) => {
                                                                    event.stopPropagation();
                                                                    setOpenRemoveTemplateConfirmation(template.id)
                                                                }}>
                                                        <CloseIcon/>
                                                    </IconButton>
                                                </Box>
                                            </MenuItem>
                                        ))}

                                    </Select>
                                </FormControl>

                                {openRemoveTemplateConfirmation && (<ConfirmDialog title={'Usuwanie szablonu'}
                                                                                   description={`Czy na pewno chcesz usunąć szablon ${mediaTemplatesData?.filter(el => el.id === openRemoveTemplateConfirmation as number)[0].name}?`}
                                                                                   confirmText={'usuń'}
                                                                                   open={!!openRemoveTemplateConfirmation}
                                                                                   onConfirm={() => {
                                                                                       removeTemplate(openRemoveTemplateConfirmation as number)
                                                                                       setOpenRemoveTemplateConfirmation(null)
                                                                                   }}
                                                                                   onCancel={() => {
                                                                                       setOpenRemoveTemplateConfirmation(null)
                                                                                   }}></ConfirmDialog>)}


                                <CheckboxTree
                                    ref={checkboxTreeRef}
                                    key={treeState.key}
                                    showNodeTitles={true}
                                    nodes={filteredNodes}
                                    checked={checkedNodes}
                                    expanded={expanded}
                                    showExpandAll={true}
                                    onCheck={(checked: any) => {
                                        setCheckedNodes(checked)
                                        updateMediaInForm(checked)
                                        setTreeState({key: Math.random()})
                                        setSelectedTemplate('')
                                    }}
                                    onExpand={(expanded: any) => {
                                        setExpanded(expanded)
                                    }}
                                /></>
                        )}
                    </Box>
                </DialogContent>
                <DialogActions>
                    <Button disabled={checkedNodes.length === 0} variant={'contained'} onClick={() => {
                        setOpenSaveTemplateModal(true)
                    }}>Zapisz jako szablon</Button>
                    <Button onClick={handleCloseNodesModal}>{t('common.closeWindow')}</Button>

                </DialogActions>


            </Dialog>

            <Dialog
                open={openUsedMediaTreeModal}
                onClose={() => {
                    setOpenUsedMediaTreeModal(false)
                }}
                fullWidth={true}
                maxWidth="md">

                <DialogTitle id="alert-dialog-title">
                    Wybrane nośniki
                </DialogTitle>
                <DialogContent>

                    {(loadingMediaTree) && (
                        <Box sx={{display: 'flex', justifyContent: 'center', alignItems: 'center', height: '300px'}}>
                            <CircularProgress size={100}/>
                        </Box>)}

                    <Box sx={{minHeight: '300px'}}>
                        {(mediaTree && checkedNodes.length > 0) ? (
                            <UsedMediaTable usedMedia={getSelectedMediaArrayFromTree(mediaTree)}
                                            buildingColumnName={'Nazwa klubu'}></UsedMediaTable>
                        ) : <p>Brak zaznaczonych nośników...</p>}
                    </Box>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => {
                        setOpenUsedMediaTreeModal(false)
                    }}>{t('common.closeWindow')}</Button>
                </DialogActions>
            </Dialog>


            {openSaveTemplateModal && (<Dialog
                open={openSaveTemplateModal}
                onClose={(value) => {

                }}
                aria-labelledby="alert-dialog-title"
                aria-describedby="alert-dialog-description"
            >
                <DialogTitle id="alert-dialog-title">
                    Zapisz nowy szablon
                </DialogTitle>
                <DialogContent>
                    <TextField
                        value={templateName}
                        label="Nazwa szablonu"
                        onChange={(event) => {
                            setTemplateName(event.target.value)
                        }}
                        required
                        InputProps={{inputProps: {min: 1}}}
                        type="text"
                        variant="outlined"
                        sx={{my: 3, width: '250px'}}></TextField>
                </DialogContent>
                <DialogActions>
                    <Button disabled={!templateName} variant={'contained'} onClick={() => {
                        createTemplate()
                    }}>{t('common.save')}</Button>
                    <Button onClick={() => {
                        setOpenSaveTemplateModal(false)
                    }} autoFocus>
                        {t('common.cancel')}
                    </Button>
                </DialogActions>
            </Dialog>)}


            <FormControl fullWidth sx={{marginTop: '45px', paddingBottom: '20px'}}>
                {tagsData && (<Autocomplete
                    multiple
                    id="tags-filled"
                    options={tagsData.map((option) => option.name)}
                    value={voohForm.tagList}
                    onChange={(_, value) => {
                        dispatch(setVoohForm({
                            ...voohForm,
                            tagList: value
                        }));
                    }}
                    freeSolo
                    renderTags={(value: readonly string[], getTagProps) =>
                        value.map((option: string, index: number) => {
                            const {key, ...tagProps} = getTagProps({index});
                            return (
                                <Chip sx={{marginY: '10px'}} color={"info"} variant="filled"
                                      label={option}
                                      key={key} {...tagProps} />
                            );
                        })
                    }
                    renderInput={(params) => (
                        <TextField
                            {...params}
                            variant="outlined"
                            label="Tagi"
                            placeholder={tagsData.length === 0 ? 'Brak utworzonych tagów... wpisz nowy i zatwierdź enterem' : 'Wybierz z listy lub wpisz nowy i zatwierdź enterem'}
                        />
                    )}
                />)}
            </FormControl>

            {!isExternal && (<TextField
                value={voohForm.nominalDuration}
                label="Referencyjny czas ramówki (w minutach)"
                onChange={(event) => {
                    setFormErrors({
                        ...formErrors,
                        nominalDuration: !event.target.value || parseInt(event.target.value) < MIN_NOMINAL_DURATION || parseInt(event.target.value) > MAX_NOMINAL_DURATION
                    })
                    dispatch(setVoohForm({
                        ...voohForm,
                        nominalDuration: parseInt(event.target.value)
                    }));
                }}
                type="number"
                variant="outlined"
                InputProps={{inputProps: {min: MIN_NOMINAL_DURATION}}}
                sx={{mt: 3, mb: 3}}
                error={formErrors.nominalDuration}
                helperText={formErrors.nominalDuration ? `Referencyjny czas ramówki musi być większy niż ${MIN_NOMINAL_DURATION - 1} minut i mniejszy niż ${MAX_NOMINAL_DURATION + 1}` : ''}
                fullWidth
            />)}

            <Divider sx={{paddingY: '20px'}} textAlign="left"><Typography
                sx={{color: 'text.secondary'}}>Uzupełnij schemat ramówki</Typography></Divider>
            {!isExternal && (<Box
                sx={{display: 'flex', flexDirection: 'row', gap: '10px', alignItems: 'center', marginBottom: '20px'}}
            ><TextField
                size={'small'}
                value={advertisingBlocksNumber}
                label="Ilość bloków reklamowych w ramówce"
                onChange={(event) => {
                    setAdvertisingBlocksNumber(parseInt(event.target.value))
                }}
                required
                InputProps={{inputProps: {min: 4}}}
                type="number"
                variant="outlined"
                sx={{width: '250px'}}
                helperText={(advertisingBlocksNumber >= MIN_ADVERTISEMENTS) ? '' : `Ilość bloków musi być większa niż ${MIN_ADVERTISEMENTS}`}
                error={advertisingBlocksNumber < MIN_ADVERTISEMENTS}/>
                <Button variant="outlined"
                        sx={{marginX: '15px'}}
                        onClick={() => {
                            setOpenSelectBumperDialog(true)
                        }}>{'Wybierz bumper'}</Button>


                {selectedBumper && (<Box sx={{display: 'flex', alignItems: 'center', gap: '10px'}}>
                    {
                        selectedBumper &&
                        (!selectedBumper.thumbnailUrl ? (
                                <DescriptionOutlinedIcon
                                    fontSize="large"/>) : (
                                <Box sx={{
                                    height: Thumbnail.width,
                                    width: Thumbnail.width,
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    background: Thumbnail.background
                                }}><img
                                    width={Thumbnail.width}
                                    height={Thumbnail.width}
                                    src={`${selectedBumper.thumbnailUrl}`}
                                    alt={selectedBumper.name}
                                    loading="lazy"/>
                                </Box>)
                        )
                    }
                    {
                        selectedBumper && (
                            <Link onClick={(event) => {
                                event.preventDefault()
                                openImageFile(selectedBumper)
                            }
                            }

                                  component={'button'}>{selectedBumper.name}</Link>
                        )
                    }
                    {selectedBumper && (<Button variant="contained"
                                                sx={{marginX: '15px'}}
                                                onClick={() => {
                                                    setVoohItems([]);

                                                    getFilesMeta([selectedBumper.id]).then(async (res: ResourceMetaData[]) => {
                                                        const bumper = {
                                                            ...res[0],
                                                            name: selectedBumper.name,
                                                            voohItemType: VoohItemType.Bumper,
                                                        }
                                                        let newRows: ScheduleRow[] = [];

                                                        scheduleRows.forEach((scheduleRow, index) => {
                                                            if (scheduleRow.voohItemType === VoohItemType.Advertisement && !scheduleRow._destroy) {
                                                                newRows.push({...bumper, uuid: getUuid(index)})
                                                                newRows.push(scheduleRow)
                                                            } else if (scheduleRow.voohItemType === VoohItemType.Bumper) {
                                                                newRows.push({...scheduleRow, _destroy: true})
                                                            } else {
                                                                newRows.push(scheduleRow)
                                                            }

                                                        })
                                                        setVoohItems(newRows)
                                                    })
                                                    // setOpenSelectBumperDialog(true)
                                                }}>{'Dodaj przed reklamy'}</Button>)}


                </Box>)}
                {
                    selectedBumper && (
                        <IconButton
                            color={'error'} aria-label="delete" onClick={() => {
                            setVoohItems([])
                            setTimeout(function () {
                                setVoohItems(scheduleRows.map((el) => {
                                    if (el.voohItemType === VoohItemType.Bumper) {
                                        return {
                                            ...el,
                                            _destroy: true
                                        }
                                    } else {
                                        return el
                                    }
                                }))

                            }, 0)
                            setSelectedBumper(null)
                        }}>
                            <ClearIcon/>
                        </IconButton>
                    )
                }
            </Box>)}

            <Box sx={{display: 'flex', alignItems: 'center', gap: '15px'}}>
                {tagsData && (<Autocomplete
                    sx={{marginTop: '15px', paddingBottom: '20px', width: '400px'}}
                    id="scheduleTimeSummary"
                    size={'small'}
                    options={tagsData.map((option) => option.name)}
                    onChange={(_, value) => {
                        setSelectedSchedulesTag(value)
                    }}
                    renderInput={(params) => (
                        <TextField
                            {...params}
                            variant="outlined"
                            label="Sumaryczny czas ramówek dla wybranego tagu"
                            placeholder={tagsData.length === 0 ? 'Brak utworzonych tagów...' : 'Wybierz z listy'}
                        />
                    )}
                />)}
                <Typography style={{fontWeight: 'bold'}}>
                    {(formatDuration(sumSecSchedulesForSelectedTag || 0))}
                </Typography>
            </Box>

            <Box sx={{display: 'flex', alignItems: 'center', gap: '15px', marginTop: '15px', marginBottom: '20px'}}>
                <FormControl sx={{minWidth: '300px'}} size="small">
                    <InputLabel id="schedule-template-select-label">Szablon ramówek</InputLabel>
                    <Select
                        labelId="schedule-template-select-label"
                        id="schedule-template-select"
                        value={selectedScheduleTemplate}
                        label="Szablon ramówek"
                        renderValue={(selected) => {
                            const selectedTemplate = scheduleTemplates.find((scheduleTemplate) => scheduleTemplate.id === selected)
                            return (
                                <Box sx={{
                                    display: 'flex',
                                    justifyContent: 'space-between',
                                    alignItems: 'center',
                                    width: '100%'
                                }}>
                                    <span>{selectedTemplate.name} ({selectedTemplate.scheduleRows.length} elementów)</span>
                                </Box>
                            )
                        }}
                        onChange={(event) => {
                            const templateId = event.target.value as number;
                            if (templateId) {
                                loadScheduleTemplate(templateId);
                            }
                        }}
                    >
                        <MenuItem value="" onClick={(event) => {
                            setSelectedScheduleTemplate('')
                        }}>
                            <em>Brak</em>
                        </MenuItem>
                        {scheduleTemplates.map((template) => (
                            <MenuItem key={template.id} value={template.id}>
                                <Box sx={{
                                    display: 'flex',
                                    justifyContent: 'space-between',
                                    alignItems: 'center',
                                    width: '100%'
                                }}>
                                    <span>{template.name} ({template.scheduleRows.length} elementów)</span>
                                    <IconButton
                                        color={'error'}
                                        edge="end"
                                        aria-label="remove"
                                        size="small"
                                        onClick={(event) => {
                                            event.stopPropagation();
                                            setOpenRemoveScheduleTemplateConfirmation(template.id);
                                        }}
                                    >
                                        <CloseIcon fontSize="small"/>
                                    </IconButton>
                                </Box>
                            </MenuItem>
                        ))}
                    </Select>
                </FormControl>
                <Button
                    variant="outlined"
                    disabled={scheduleRows.filter(row => !row._destroy).length === 0 || !!selectedScheduleTemplate}
                    onClick={() => setOpenSaveScheduleTemplateModal(true)}
                >
                    Zapisz szablon ramówek
                </Button>
            </Box>

            {openReplanningConfirmation && (
                <ConfirmDialog
                    title={'Replanowanie ramówki'}
                    description={'Czy chcesz wykonać replanowanie dla tej ramówki?'}
                    confirmText={'replanuj'}
                    open={openReplanningConfirmation}
                    loading={replanningInProgress}
                    onConfirm={confirmReplanning}
                    onCancel={() => {
                        setOpenReplanningConfirmation(false);
                    }}
                />
            )}

            {openRemoveScheduleTemplateConfirmation && (
                <ConfirmDialog
                    title={'Usuwanie szablonu ramówek'}
                    description={`Czy na pewno chcesz usunąć szablon ${scheduleTemplates.find(el => el.id === openRemoveScheduleTemplateConfirmation)?.name}?`}
                    confirmText={'usuń'}
                    open={!!openRemoveScheduleTemplateConfirmation}
                    onConfirm={() => {
                        deleteScheduleTemplate(openRemoveScheduleTemplateConfirmation as number);
                        setOpenRemoveScheduleTemplateConfirmation(null);
                    }}
                    onCancel={() => {
                        setOpenRemoveScheduleTemplateConfirmation(null);
                    }}
                />
            )}


            {openSelectBumperDialog && (
                <MediabankFileSelectDialog
                    mediaFormat={MediaFormat.moveTv}
                    onSelectMediabankFile={(bumperFile) => {
                        setSelectedBumper(bumperFile);
                        setOpenSelectBumperDialog(false)

                    }}
                    openMediabankDialog={openSelectBumperDialog}
                    selectedMediabankResource={selectedBumper}
                    onClose={() => {
                        setOpenSelectBumperDialog(false)
                    }}
                ></MediabankFileSelectDialog>
            )}

            {openSaveScheduleTemplateModal && (
                <Dialog
                    open={openSaveScheduleTemplateModal}
                    onClose={() => setOpenSaveScheduleTemplateModal(false)}
                    aria-labelledby="save-schedule-template-dialog"
                    aria-describedby="save-schedule-template-description"
                >
                    <DialogTitle id="save-schedule-template-dialog">
                        Utwórz nowy szablon ramówek
                    </DialogTitle>
                    <DialogContent>
                        <TextField
                            value={scheduleTemplateName}
                            label="nazwa szablonu dla ramówek"
                            onChange={(event) => {
                                setScheduleTemplateName(event.target.value)
                            }}
                            required
                            type="text"
                            variant="outlined"
                            sx={{my: 3, width: '350px'}}
                        />
                    </DialogContent>
                    <DialogActions>
                        <Button
                            disabled={!scheduleTemplateName.trim()}
                            variant={'contained'}
                            onClick={saveScheduleTemplate}
                        >
                            {t('common.save')}
                        </Button>
                        <Button onClick={() => {
                            setOpenSaveScheduleTemplateModal(false);
                            setScheduleTemplateName('');
                        }}>
                            {t('common.cancel')}
                        </Button>
                    </DialogActions>
                </Dialog>
            )}


            <Box sx={{display: 'flex', justifyContent: 'space-between', gap: '20px'}}>
                <VoohDragAndDropTable
                    bumper={selectedBumper}
                    isExternal={isExternal}
                    hideMediabank={false}
                    onFullScreenClick={() => {
                        setFullScreen(true)
                    }}
                    handleDragEnd={handleDragEnd}
                    scheduleRows={scheduleRows}
                    setVoohItems={setVoohItems}
                    setRollbackAdvNumber={setRollbackAdvNumber}
                    setAdvertisingBlocksNumber={setAdvertisingBlocksNumber}
                    voohCategories={voohCategories}
                    advertisingBlocksNumber={advertisingBlocksNumber}></VoohDragAndDropTable>


            </Box>
            {fullScreen && (<Dialog
                fullScreen
                open={fullScreen}
                onClose={(value) => {

                }}
                aria-labelledby="alert-dialog-title"
                aria-describedby="alert-dialog-description"
            >
                <AppBar sx={{position: 'relative', marginBottom: '30px'}} color="default">
                    <Toolbar>
                        <IconButton
                            edge="start"
                            color="inherit"
                            onClick={() => {
                                setFullScreen(false)
                            }}
                            aria-label="close"
                        >
                            <CloseIcon/>
                        </IconButton>
                    </Toolbar>
                </AppBar>
                <VoohDragAndDropTable
                    bumper={selectedBumper}
                    isExternal={isExternal}
                    fullScreenMode={true}
                    hideMediabank={true}
                    onFullScreenClick={() => {
                        setFullScreen(true)
                    }}
                    handleDragEnd={handleDragEnd}
                    scheduleRows={scheduleRows}
                    setVoohItems={setVoohItems}
                    setRollbackAdvNumber={setRollbackAdvNumber}
                    setAdvertisingBlocksNumber={setAdvertisingBlocksNumber}
                    voohCategories={voohCategories}
                    advertisingBlocksNumber={advertisingBlocksNumber}></VoohDragAndDropTable>
            </Dialog>)}


            {openWarningAfterChangingAdvNumber && (<Dialog
                open={openWarningAfterChangingAdvNumber}
                onClose={(value) => {

                }}
                aria-labelledby="alert-dialog-title"
                aria-describedby="alert-dialog-description"
            >
                <DialogTitle id="alert-dialog-title">
                    Ostrzeżenie
                </DialogTitle>
                <DialogContent>
                    <Typography
                        sx={{color: 'text.primary', paddingY: '10px'}}>Zmiana ilość bloków reklamowych usunie dodane
                        ramówki, czy na pewno chesz kontynuować?</Typography>
                </DialogContent>
                <DialogActions>
                    <Button variant={'contained'} onClick={() => {
                        setVoohItems([])
                        setOpenWarningAfterChangingAdvNumber(false);
                        setAdvertisingBlocksNumberPrevValue(advertisingBlocksNumber)
                        const advertisements: ScheduleRow[] = [...Array(advertisingBlocksNumber).keys()].map((_, index) => {
                            return {
                                name: 'REKLAMA',
                                uuid: getUuid(index),
                                voohItemType: VoohItemType.Advertisement,
                                duration: 0,
                                categoryId: null
                            }
                        })
                        setTimeout(() => {
                            setVoohItems([...advertisements, ...scheduleRows.map((el) => {
                                return {
                                    ...el,
                                    _destroy: true
                                }
                            })])
                        }, 0)

                    }}>Tak, zmień ilość</Button>
                    <Button onClick={() => {
                        setOpenWarningAfterChangingAdvNumber(false);
                        setAdvertisingBlocksNumber(advertisingBlocksNumberPrevValue)
                        setRollbackAdvNumber(true)
                    }} autoFocus>
                        {t('common.cancel')}
                    </Button>
                </DialogActions>
            </Dialog>)}

            <Divider sx={{paddingY: '10px'}}></Divider>

            <Box sx={{marginTop: '20px'}}><OwnershipSelect ownershipsAttributes={voohForm.ownershipsAttributes}
                                                           onDelete={(val) => {
                                                               const {principalType, principalId} = parseOwnershipPrincipalKey(val);
                                                               dispatch(setVoohForm({
                                                                   ...voohForm,
                                                                   ownershipsAttributes: voohForm.ownershipsAttributes.filter(
                                                                       person => !(person.principalType === principalType && person.principalId === principalId)
                                                                   )
                                                               }));
                                                           }}

                                                           onAdd={addAuthorizedPerson}
                                                           onDeleteAll={() => {
                                                               dispatch(setVoohForm({
                                                                   ...voohForm,
                                                                   ownershipsAttributes: []
                                                               }));
                                                           }}
            ></OwnershipSelect></Box>


            <Box sx={{display: 'flex', justifyContent: 'end', padding: '20px'}}>
                {canSaveVooh && (<Button variant={'outlined'} onClick={saveForm}>{t('common.save')}</Button>)}
            </Box>

        </form>


        {(duplicatedRowsData.duplicatedRows.length > 0) && (<Dialog
            open={duplicatedRowsData.duplicatedRows.length > 0}
            onClose={(value) => {

            }}
            aria-labelledby="alert-dialog-title"
            aria-describedby="alert-dialog-description"
        >
            <DialogTitle id="alert-dialog-title">
                Ostrzeżenie
            </DialogTitle>
            <DialogContent>
                <Typography
                    sx={{color: 'text.primary', paddingY: '10px'}}>Poniższe pliki już istnieją w ramówce</Typography>
                <ul>
                    {duplicatedRowsData.duplicatedRows.map((row, i) => {
                        return (<li>
                            {row.name}
                        </li>)
                    })}

                </ul>
                <Typography
                    sx={{color: 'text.primary', paddingY: '10px'}}>Czy chcesz je mimo to dodać?</Typography>

            </DialogContent>
            <DialogActions>
                <Button variant={'contained'} onClick={() => {
                    getFilesMeta(duplicatedRowsData.newRows).then(async (res: ResourceMetaData[]) => {
                        placeNewRowsInVoohSchedule(res, duplicatedRowsData.mediabankResources, duplicatedRowsData.destinationIndex);
                        setDuplicatedRowsData(duplicatedRowsInitialData)
                    })
                }}>Tak, dodaj ramówki</Button>
                <Button onClick={() => {
                    setDuplicatedRowsData(duplicatedRowsInitialData)
                }} autoFocus>
                    {t('common.cancel')}
                </Button>
            </DialogActions>
        </Dialog>)}


    </>
}

export default VoohForm;



